import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify, useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { browser, googleConnectionApi } from "./googleConnectionApi";
import {
  confirmErrorKey,
  readGoogleReturn,
  stripGoogleReturn,
} from "./googleConnectionLogic";

const QUERY_KEY = ["google-connection"];

/** Own Google account (Gmail, Calendar, Drive) for the Volt assistant: status, connect, disconnect. */
export const GoogleConnectionCard = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Read once on mount: code and state from the callback leave the URL in the first effect, before any request.
  const [googleReturn] = useState(() => readGoogleReturn(location.search));
  const confirmSent = useRef(false);

  const confirm = useMutation({
    mutationFn: googleConnectionApi.confirm,
    onSuccess: (connection) => {
      queryClient.setQueryData(QUERY_KEY, connection);
      notify("crm.profile.google.result_connected", { type: "success" });
    },
    onError: (error) => notify(confirmErrorKey(error), { type: "error" }),
  });
  const confirmGrant = confirm.mutate;

  // The status waits for the confirm, so nothing is requested while code and state are still in the URL.
  const { data, isPending, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: googleConnectionApi.status,
    enabled:
      googleReturn?.kind !== "confirm" || confirm.isSuccess || confirm.isError,
  });

  useEffect(() => {
    if (!readGoogleReturn(location.search)) return;
    navigate(
      {
        pathname: location.pathname,
        search: stripGoogleReturn(location.search),
      },
      { replace: true },
    );
  }, [location, navigate]);

  // Once only (StrictMode runs effects twice): Google accepts a code a single time.
  useEffect(() => {
    if (!googleReturn || confirmSent.current) return;
    confirmSent.current = true;
    if (googleReturn.kind === "error") {
      notify("crm.profile.google.result_error", { type: "error" });
      return;
    }
    confirmGrant({ code: googleReturn.code, state: googleReturn.state });
  }, [googleReturn, confirmGrant, notify]);

  const connect = useMutation({
    mutationFn: googleConnectionApi.start,
    onSuccess: ({ url }) => browser.open(url),
    onError: () => notify("crm.profile.google.result_error", { type: "error" }),
  });

  const disconnect = useMutation({
    mutationFn: googleConnectionApi.disconnect,
    onSuccess: () => {
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      notify("crm.profile.google.disconnected", { type: "success" });
    },
    onError: () => {
      setConfirmOpen(false);
      notify("crm.profile.google.disconnect_error", { type: "error" });
    },
  });

  const needsReconnect = !!data?.connected && data.missingScopes.length > 0;

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("crm.profile.google.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.profile.google.description")}
          </p>
          {isPending ? null : isError ? (
            <p className="text-sm text-destructive">
              {translate("crm.profile.google.load_error")}
            </p>
          ) : (
            <>
              <p className="text-sm">
                {data.connected
                  ? translate("crm.profile.google.status_connected", {
                      email: data.googleEmail ?? "",
                    })
                  : translate("crm.profile.google.status_disconnected")}
              </p>
              {needsReconnect && (
                <p className="text-sm text-destructive">
                  {translate("crm.profile.google.missing_scopes")}
                </p>
              )}
              <div className="flex flex-row justify-end gap-2">
                {data.connected && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmOpen(true)}
                    disabled={disconnect.isPending}
                  >
                    {translate("crm.profile.google.disconnect")}
                  </Button>
                )}
                {(!data.connected || needsReconnect) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => connect.mutate()}
                    disabled={connect.isPending}
                  >
                    {translate(
                      needsReconnect
                        ? "crm.profile.google.reconnect"
                        : "crm.profile.google.connect",
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
        <Confirm
          isOpen={confirmOpen}
          loading={disconnect.isPending}
          title="crm.profile.google.confirm_title"
          content="crm.profile.google.confirm_content"
          confirm="crm.profile.google.disconnect"
          confirmColor="warning"
          onConfirm={() => disconnect.mutate()}
          onClose={() => setConfirmOpen(false)}
        />
      </CardContent>
    </Card>
  );
};
